// src/components/lead/sections/ProfileEditor.tsx
import { useState } from "react";
import type { Location } from "@/types/location";
import type { OrgLocation } from "@/config/locations";
import { useAuthOptional } from "@/contexts/AuthContext";
import { ProfileEditorVoiceBlock } from "./ProfileEditorVoiceBlock";
import { saveSignedInIdentity, type Salesperson } from "./profileEditorIdentity";

export type { Salesperson };

export function ProfileEditor({
  salesperson,
  setSalesperson,
  location,
  setLocation,
  locationsLoaded,
  locations,
  refreshLocations,
  voiceEnabled,
  setVoiceEnabled,
  voiceProvider,
  setVoiceProvider,
  speakAnswers,
  canSpeakHelp,
  setSpeakMode,
}: {
  salesperson: Salesperson;
  setSalesperson: (s: Salesperson | null) => void;
  location: Location | null;
  setLocation: (l: Location) => void;
  locationsLoaded: boolean;
  locations: OrgLocation[];
  refreshLocations: (reason?: string, opts?: { force?: boolean }) => void;
  voiceEnabled: boolean;
  setVoiceEnabled: (v: boolean) => void;
  voiceProvider: "system" | "custom";
  setVoiceProvider: (v: "system" | "custom") => void;
  speakAnswers: boolean;
  canSpeakHelp: boolean;
  setSpeakMode: (m: "answers" | "help" | "all") => void;
}) {
  const auth = useAuthOptional();
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountMessage, setAccountMessage] = useState("");

  return (
    <div className="mb-6 text-left text-sm md:text-base bg-gray-50 border border-gray-200 rounded-2xl p-4">
      <h2 className="font-semibold text-gray-800 mb-2">Profile &amp; lot settings</h2>

      {auth?.session ? (
        <div className="mb-3 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
          Signed-in identity fields save back to your account profile so Knowledge Base tools and user details stay in sync.
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Your name</label>
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={salesperson.name}
            onChange={(e) => setSalesperson({ ...salesperson, name: e.target.value })}
            onBlur={() => {
              if (!auth?.session || !auth.profile) return;
              void saveSignedInIdentity({
                session: auth.session,
                profile: auth.profile,
                nextSalesperson: salesperson,
                refreshProfile: auth.refreshProfile,
                setAccountSaving,
                setAccountMessage,
              });
            }}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Cell (optional)</label>
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="Your cell number"
            value={salesperson.cell}
            onChange={(e) => setSalesperson({ ...salesperson, cell: e.target.value })}
            onBlur={() => {
              if (!auth?.session || !auth.profile) return;
              void saveSignedInIdentity({
                session: auth.session,
                profile: auth.profile,
                nextSalesperson: salesperson,
                refreshProfile: auth.refreshProfile,
                setAccountSaving,
                setAccountMessage,
              });
            }}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Email (optional)</label>
          <input
            className="w-full border rounded-lg px-3 py-2 text-sm"
            placeholder="Your email"
            value={salesperson.email}
            onChange={(e) => setSalesperson({ ...salesperson, email: e.target.value })}
            onBlur={() => {
              if (!auth?.session || !auth.profile) return;
              void saveSignedInIdentity({
                session: auth.session,
                profile: auth.profile,
                nextSalesperson: salesperson,
                refreshProfile: auth.refreshProfile,
                setAccountSaving,
                setAccountMessage,
              });
            }}
          />
        </div>

        {auth?.session ? (
          <div className="md:col-span-2 text-xs text-gray-500">
            {accountSaving ? "Saving account details…" : accountMessage || "Signed-in changes here are mirrored to your account profile."}
          </div>
        ) : null}

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Lot / location</label>
          <select
            disabled={!locationsLoaded}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={location?.id || ""}
            onChange={(e) => {
              const newId = e.target.value;
              const loc = locations.find((l) => l.id === newId);
              if (!loc) return;
              setLocation({
                id: loc.id,
                name: loc.fullName,
                address: loc.address,
                phone: loc.phone || "",
              });
            }}
          >
            <option value="">{locationsLoaded ? "Select lot…" : "Loading locations..."}</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.fullName}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <ProfileEditorVoiceBlock
            voiceEnabled={voiceEnabled}
            setVoiceEnabled={setVoiceEnabled}
            voiceProvider={voiceProvider}
            setVoiceProvider={setVoiceProvider}
            speakAnswers={speakAnswers}
            canSpeakHelp={canSpeakHelp}
            setSpeakMode={setSpeakMode}
          />
        </div>

        <div className="md:col-span-2 mt-2">
          <button
            type="button"
            className="w-full text-left border rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
            onClick={() => refreshLocations("profile-editor", { force: true })}
            disabled={!navigator.onLine || !locationsLoaded}
            title={
              !navigator.onLine ? "Offline" : !locationsLoaded ? "Locations are still loading" : "Refresh from the server"
            }
          >
            Refresh Locations List
          </button>

          <div className="text-xs text-gray-500 mt-1">Note: the server may cache Sheets results for a few minutes.</div>
        </div>
      </div>
    </div>
  );
}
